// ═══════════════════════════════════════════════════════════════
// Handoff — Dashboard
// Key metrics, incoming handoffs, active incidents
// Uses shared incidents from authStore
// ═══════════════════════════════════════════════════════════════

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, SimpleGrid, Group, Stack, Badge, Button,
  Loader, Center,
} from '../../components/gab/GabComponents';
import {
  IconArrowsExchange, IconCalendarEvent, IconChecklist,
  IconAlertTriangle, IconClock,
} from '@tabler/icons-react';
import { useHandoffStore } from '../../store/handoffStore';
import { useAuthStore } from '../../store/authStore';
import { semantic } from '../../lib/colors';

export default function HandoffDashboard() {
  const navigate = useNavigate();
  const { incidents, activeIncident, loadIncidents } = useAuthStore();
  const { notes, actionItems, positions, events: handoffEvents, loading, fetchAll } = useHandoffStore();
  useEffect(() => {
    loadIncidents();
    fetchAll();
  }, []);

  if (loading) {
    return <Center p="xl"><Loader /></Center>;
  }

  // Store already filtered to active incident via fetchAll(incidentId)
  const incNotes = notes;
  const incActions = actionItems;
  const incEvents = handoffEvents;

  const pendingNotes = incNotes.filter(n => n.status === 'submitted');
  const overdueActions = incActions.filter(a =>
    a.status === 'pending' && a.dueDate && new Date(a.dueDate) < new Date()
  );
  const pendingActions = incActions.filter(a => a.status === 'pending');

  // Incoming handoffs (submitted)
  const incoming = incNotes.filter(n => n.status === 'submitted');

  return (
    <div>
      <Title order={3} mb="md" c="black">Handoff Dashboard</Title>

      {/* Key Metrics */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
        <Paper p="md" radius="sm" withBorder ta="center">
          <IconCalendarEvent size={20} color="var(--blurple)" style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700}>{incEvents.filter(e => e.status === 'active').length}</Text>
          <Text size="xs" c="dimmed">Active Events</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <IconArrowsExchange size={20} color={semantic.warning} style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700} c={semantic.warning}>{pendingNotes.length}</Text>
          <Text size="xs" c="dimmed">Pending Handoffs</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <IconAlertTriangle size={20} color={semantic.error} style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700} c={semantic.error}>{overdueActions.length}</Text>
          <Text size="xs" c="dimmed">Overdue Actions</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <IconChecklist size={20} color={semantic.info} style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700} c={semantic.info}>{pendingActions.length}</Text>
          <Text size="xs" c="dimmed">Pending Actions</Text>
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
        {/* Incoming Handoffs */}
        <Paper p="md" radius="sm" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={600} size="sm">Incoming Handoffs</Text>
            <Button variant="subtle" size="xs" onClick={() => navigate('/handoff/notes')}>View All</Button>
          </Group>
          {incoming.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="md">No pending handoffs</Text>
          ) : (
            <Stack gap="xs">
              {incoming.slice(0, 5).map(n => {
                const hEvt = handoffEvents.find(e => e.id === Number(n.eventId));
                const inc = hEvt?.incidentId != null ? incidents.find(e => e.id === Number(hEvt.incidentId)) : undefined;
                const eventLabel = inc?.name || hEvt?.eventName || `Event #${n.eventId}`;
                return (
                  <Paper key={n.id} p="sm" radius="sm" withBorder
                    style={{ cursor: 'pointer' }} onClick={() => navigate(`/handoff/note/${n.id}`)}>
                    <Group justify="space-between">
                      <div>
                        <Text size="sm" fw={500}>{n.positionName}</Text>
                        <Text size="xs" c="dimmed">{eventLabel} — {n.fromShift} → {n.toShift}</Text>
                      </div>
                      <Group gap="xs">
                        <Badge size="xs" color="#C68700">Pending</Badge>
                        <Text size="xs" c="dimmed">{n.datecreated ? new Date(n.datecreated).toLocaleDateString() : ''}</Text>
                      </Group>
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>

        {/* Active Handoff Events */}
        <Paper p="md" radius="sm" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={600} size="sm">Active Handoff Events</Text>
            <Button variant="subtle" size="xs" onClick={() => navigate('/handoff/events')}>View All</Button>
          </Group>
          {incEvents.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="md">No handoff events</Text>
          ) : (
            <Stack gap="xs">
              {incEvents.filter(e => e.status === 'active').slice(0, 5).map(hEvt => {
                const noteCount = incNotes.filter(n => n.eventId === hEvt.id).length;
                const actionCount = incActions.filter(a => a.eventId === hEvt.id && a.status === 'pending').length;
                const linkedInc = hEvt.incidentId != null ? incidents.find(i => i.id === Number(hEvt.incidentId)) : undefined;
                return (
                  <Paper key={hEvt.id} p="sm" radius="sm" withBorder
                    style={{ cursor: 'pointer' }} onClick={() => navigate(`/handoff/event/${hEvt.id}`)}>
                    <Group justify="space-between">
                      <div>
                        <Text size="sm" fw={500}>{hEvt.eventName}</Text>
                        <Text size="xs" c="dimmed">{linkedInc?.name || `Event #${hEvt.id}`}</Text>
                      </div>
                      <Group gap="xs">
                        <Badge size="xs" variant="light" color="blurple">{noteCount} notes</Badge>
                        {actionCount > 0 && (
                          <Badge size="xs" variant="light" color="#03AC9E">{actionCount} actions</Badge>
                        )}
                      </Group>
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>
      </SimpleGrid>

      {/* Overdue Action Items */}
      {overdueActions.length > 0 && (
        <Paper p="md" radius="sm" withBorder style={{ border: '1px solid var(--color-error)' }}>
          <Group gap="xs" mb="sm">
            <IconClock size={16} color={semantic.error} />
            <Text fw={600} size="sm" c={semantic.error}>Overdue Action Items</Text>
          </Group>
          <Stack gap="xs">
            {overdueActions.slice(0, 5).map(a => {
              const hEvt = handoffEvents.find(e => e.id === Number(a.eventId));
              const inc = hEvt?.incidentId != null ? incidents.find(e => e.id === Number(hEvt.incidentId)) : undefined;
              const eventLabel = inc?.name || hEvt?.eventName || `Event #${a.eventId}`;
              return (
                <Group key={a.id} justify="space-between">
                  <div>
                    <Text size="sm" fw={500}>{a.title}</Text>
                    <Text size="xs" c="dimmed">{eventLabel} — Assigned: {a.assignedTo || 'Unassigned'}</Text>
                  </div>
                  <Badge size="xs" color="var(--color-error)">
                    Due {a.dueDate ? new Date(a.dueDate).toLocaleDateString() : ''}
                  </Badge>
                </Group>
              );
            })}
          </Stack>
        </Paper>
      )}
    </div>
  );
}
